import VueRouter, { Route } from 'vue-router';

import { modalService } from '@gig/magic-common';
import { RouteName } from '../../../routes/routes.constants';
import { config } from '../../config';
import {
    WalletViewRouteName,
    isWalletAccessAllowedBySoW,
    SOW_WALLET_ACCESS,
} from '@gig/magic-wallet';
import { magic } from '@gig/magic-sdk';
import type { IGetAmlStatusResponse } from '@gig/magic-sdk/dist/adapter/users/getAmlStatus';
import { AML_STATUS_QUERY, GRACE_PERIOD_QUERY } from '@gig/magic-messages';
import type { IGracePeriod } from '@gig/magic-sdk/dist/adapter/users/getGracePeriod';
import {
    getGracePeriodDaysLeft,
    isKYCRequestedByAML,
} from '@gig/magic-jurisdictions';

export function isSOWHidden(route: Route) {
    return route.matched.some(({ meta }) => meta.allowWithPendingGracePeriod);
}

export function allowWithPendingGracePeriod(
    allowWithPendingGracePeriod: boolean = true,
) {
    return {
        allowWithPendingGracePeriod,
    };
}

export function initSowGuard(router: VueRouter) {
    router.beforeResolve(async (to, _, next) => {
        if (!magic.session.getUser()) {
            return next();
        }

        if (!isSoWEnabled() || !isAccessingWallet(to)) {
            return next();
        }

        const currentView = getCurrentWalletView(to);
        const result = await isWalletAccessAllowedBySoW(currentView);

        if (result === SOW_WALLET_ACCESS.OK) {
            next();
        } else if (result === SOW_WALLET_ACCESS.REDIRECT_TO_HOME) {
            next({ name: RouteName.HOME });
        } else if (result === SOW_WALLET_ACCESS.SOW_BLOCK_MODAL) {
            openSoWBlockModal();
        }
    });
}

export function initGracePeriodGuard(router: VueRouter) {
    router.beforeResolve(async (to, _, next) => {
        if (!magic.session.getUser() || !(await hasGracePeriodExpired())) {
            return next();
        }

        if (isSOWHidden(to) || to.name === RouteName.HOME) {
            return next();
        }

        if (isExpiredModalOpen()) {
            return next(
                new Error(
                    'Navigation canceled due to user block (grace period expired)',
                ),
            );
        }

        return next({
            name: RouteName.HOME,
        });
    });
}

async function hasGracePeriodExpired() {
    const [amlStatus, periods] = await Promise.all([
        window.queryBus.query<IGetAmlStatusResponse>(AML_STATUS_QUERY)!,
        window.queryBus.query<IGracePeriod[]>(GRACE_PERIOD_QUERY)!,
    ]);
    const daysLeft = getGracePeriodDaysLeft(periods);
    const status = amlStatus?.status;
    return !daysLeft && status && isKYCRequestedByAML(status);
}

function isExpiredModalOpen() {
    return (
        document.getElementsByTagName('magic-sow-countdown-prompt').length != 0
    );
}

function isSoWEnabled() {
    return config && config.sow && config.sow.enabled;
}

function isAccessingWallet(to: Route) {
    return to.name === RouteName.WALLET || to.fullPath.indexOf('wallet/') > -1;
}

function getCurrentWalletView(to: Route) {
    return [
        WalletViewRouteName.WITHDRAW,
        WalletViewRouteName.DEPOSIT,
        WalletViewRouteName.HISTORY,
        WalletViewRouteName.BONUSES,
    ].find((view) => isCurrentWalletView(to, view))!;
}

function isCurrentWalletView(to: Route, view: WalletViewRouteName) {
    return to.params.view === view || to.fullPath.indexOf(view) > -1;
}

function openSoWBlockModal() {
    const sowBlockModal = document.createElement('magic-sow-block-modal');
    modalService.open('sow-block-modal.title', sowBlockModal, {
        onClose: () => {
            modalService.open(
                'sow-countdown-prompt.header',
                document.createElement('magic-sow'),
            );
        },
    });
}
